import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChangeOrderWarning } from '@/hooks/useChangeOrderWarning';
import ChangeOrderWarningDialog from './components/ChangeOrderWarningDialog';
import EnhancedInvoiceForm from './EnhancedInvoiceForm';
import { Loader2 } from 'lucide-react';

interface InvoiceCreationGuardProps {
  estimateId: string;
}

const InvoiceCreationGuard: React.FC<InvoiceCreationGuardProps> = ({ estimateId }) => {
  const [showWarning, setShowWarning] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);
  const [canProceed, setCanProceed] = useState(false);
  const navigate = useNavigate();
  
  const { 
    pendingChangeOrders, 
    checkForPendingChangeOrders, 
    approveChangeOrdersAndMakeMain, 
    isLoading 
  } = useChangeOrderWarning();

  useEffect(() => {
    const checkChangeOrders = async () => {
      if (!estimateId || hasChecked) return;
      
      const changeOrders = await checkForPendingChangeOrders(estimateId);
      setHasChecked(true);
      
      if (changeOrders.length > 0) {
        setShowWarning(true);
      } else {
        setCanProceed(true);
      }
    };

    checkChangeOrders();
  }, [estimateId, checkForPendingChangeOrders, hasChecked]);

  const handleCancel = () => {
    navigate(`/estimates/${estimateId}`);
  };

  const handleContinueAnyway = () => {
    setShowWarning(false);
    setCanProceed(true);
  };

  const handleApproveChangeOrder = async () => {
    const changeOrderIds = pendingChangeOrders.map(co => co.id);
    const success = await approveChangeOrdersAndMakeMain(estimateId, changeOrderIds);
    if (success) {
      setShowWarning(false);
      setCanProceed(true);
    }
  };

  // Show loading while checking for change orders
  if (!hasChecked && isLoading) {
    return (
      <div className="container py-6 flex items-center justify-center">
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Checking for pending changes...</span>
        </div>
      </div>
    );
  }

  // Show warning dialog if there are pending change orders
  if (showWarning) {
    return (
      <>
        <div className="container py-6">
          <div className="text-center">
            <p className="text-muted-foreground">Checking for pending changes before creating invoice...</p>
          </div>
        </div>
        <ChangeOrderWarningDialog
          isOpen={showWarning}
          onClose={handleCancel}
          onContinue={handleContinueAnyway}
          onApproveChangeOrder={handleApproveChangeOrder}
          changeOrderCount={pendingChangeOrders.length}
        />
      </>
    );
  }

  // Show invoice form once checks are complete and user can proceed
  if (canProceed) {
    return <EnhancedInvoiceForm estimateId={estimateId} />;
  }

  // Fallback loading state
  return (
    <div className="container py-6 flex items-center justify-center">
      <div className="flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Loading invoice form...</span>
      </div>
    </div>
  );
};

export default InvoiceCreationGuard;